import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';

import {
  faCircle as faCircleFull, faCircleHalfStroke, faEarthAmerica, faEyeSlash, faGlobe, faSatelliteDish
} from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faBuildoutLogo } from 'helpers/custom_fontawesome_icons';

import MarketingChannel from './property_edit_form/marketing_channel';
import HideInternallyCheckbox from './property_edit_form/hide_internally_checkbox';

const STATUSES_FOR_STATES = {
  '0': ['none'],
  '1': ['none', 'connect', 'plugin', 'syndication'],
  '2': ['none', 'connect', 'plugin'],
  '3': ['none'],
  '4': ['none']
};

export default function SaleMarketingVisibility({
  closedListingPlugin,
  hideInternally,
  hideSyndication,
  initialPluginShowInSearch,
  statusInputName,
  pushToConnect,
  showHideInternallyForStatuses,
  syndication,
  syndicationDisconnectMessage,
  onHideInternallyChange,
  onPushToConnectChange,
  onSyndicationChange
}) {
  function getSaleStatus(statusInputName) {
    return document.querySelector(`input[name="${statusInputName}"]:checked`).value;
  }

  const [pluginShowInSearch, setPluginShowInSearch] = useState(initialPluginShowInSearch);
  const [saleListingStatus, setSaleListingStatus] = useState(getSaleStatus(statusInputName));

  useEffect(() => {
    const statusChangeHandler = (e) => {
      setSaleListingStatus(e.target.value);
    };

    document.querySelectorAll(`input[name="${statusInputName}"]`).forEach((input) => {
      input.addEventListener('change', statusChangeHandler);
    });
    return () => {
      document.querySelectorAll(`input[name="${statusInputName}"]`).forEach((input) => {
        input.removeEventListener('change', statusChangeHandler);
      });
    };
  }, [statusInputName]);

  const closedStatusAndClosedPlugin = saleListingStatus == '3' && closedListingPlugin;
  const enablePluginToggle = STATUSES_FOR_STATES[saleListingStatus].includes('plugin') || closedStatusAndClosedPlugin;

  const effectiveConnectValue = STATUSES_FOR_STATES[saleListingStatus].includes('connect') && pushToConnect;
  const effectiveSyndicationValue = STATUSES_FOR_STATES[saleListingStatus].includes('syndication') && syndication;
  const effectivePluginValue = enablePluginToggle && pluginShowInSearch;
  const effectiveNoneValue = !effectiveConnectValue && !effectivePluginValue && !effectiveSyndicationValue;

  const setPushToConnect = (value) => {
    onPushToConnectChange(value);
    if (value) onSyndicationChange(false);
  };

  const setSyndication = (value) => {
    onSyndicationChange(value);
    if (value) onPushToConnectChange(false);
  };

  const disableAll = () => {
    onSyndicationChange(false);
    onPushToConnectChange(false);
    setPluginShowInSearch(false);
  };

  const connectToggleDisabled = effectiveSyndicationValue;
  return (
    <div>
      <div className="mb-5" name="root-component">
        <div className="mb-3 fw-semibold">Select the automated marketing channels to enable:</div>

        <MarketingChannel
          disabled={false}
          icon={faEyeSlash}
          name="none"
          selected={effectiveNoneValue}
          signalIcon={faCircle}
          signalText="Fully Private"
          subtitle="I want to market directly to my own contacts (network) or nowhere at this time."
          title="None"
          onToggle={disableAll}
        />

        <MarketingChannel
          disabled={connectToggleDisabled || !STATUSES_FOR_STATES[saleListingStatus].includes('connect')}
          disabledTooltip={connectToggleDisabled ? 'Listings are only eligible for the Buildout Buyer Network when Syndication is turned off' : ''}
          icon={faBuildoutLogo}
          name="connect"
          selected={effectiveConnectValue}
          signalIcon={faCircleHalfStroke}
          signalText="Private"
          subtitle="I want to market directly to Buildout's private buyer network. Note: This is best designed for double-ending deals."
          title="Buildout Buyer Network"
          onToggle={() => setPushToConnect(!effectiveConnectValue)}
        />

        <MarketingChannel
          disabled={!enablePluginToggle}
          icon={faGlobe}
          name="plugin"
          selected={effectivePluginValue}
          signalIcon={faCircleFull}
          signalText="Public"
          subtitle="I want to increase exposure and market on my company's website using the Buildout plugin."
          title="My Brokerage Website"
          onToggle={() => setPluginShowInSearch(!effectivePluginValue)}
        />

        {!hideSyndication && (
          <div>
            <MarketingChannel
              disabled={!STATUSES_FOR_STATES[saleListingStatus].includes('syndication')}
              icon={faSatelliteDish}
              name="syndication"
              selected={effectiveSyndicationValue}
              signalIcon={faEarthAmerica}
              signalText="Fully Public"
              subtitle="I want to maximize exposure to all parties via Buildout's available sale listing sites."
              title="Buildout Syndication Network"
              onToggle={() => setSyndication(!effectiveSyndicationValue)}
            />

            {syndicationDisconnectMessage && (
              <p className="help-block strong" name="syndicationDisconnectMessage">
                {Parser(syndicationDisconnectMessage)}
              </p>
            )}
          </div>
        )}

        <input name="property[push_to_connect]" type="hidden" value={pushToConnect ? '1' : '0'} />
        <input
          name="property[sale_plugin_listing_attributes][show_in_search]"
          type="hidden"
          value={pluginShowInSearch ? '1' : '0'}
        />
      </div>

      {showHideInternallyForStatuses.includes(parseInt(saleListingStatus)) && (
        <div className="mb-4">
          <HideInternallyCheckbox checked={hideInternally} onChange={onHideInternallyChange} />
        </div>
      )}
    </div>
  );
}

SaleMarketingVisibility.propTypes = {
  closedListingPlugin: PropTypes.bool,
  hideInternally: PropTypes.bool,
  hideSyndication: PropTypes.bool.isRequired,
  initialPluginShowInSearch: PropTypes.bool.isRequired,
  pushToConnect: PropTypes.bool,
  showHideInternallyForStatuses: PropTypes.arrayOf(PropTypes.number).isRequired,
  statusInputName: PropTypes.string.isRequired,
  syndication: PropTypes.bool.isRequired,
  syndicationDisconnectMessage: PropTypes.string,
  onHideInternallyChange: PropTypes.func.isRequired,
  onPushToConnectChange: PropTypes.func.isRequired,
  onSyndicationChange: PropTypes.func.isRequired
};
